import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, Database, Users, Eye, EyeOff, CheckCircle, X, Plus, MoreVertical } from "lucide-react";

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [activeView, setActiveView] = useState("clearance");
  const [submissions, setSubmissions] = useState([]);
  const [syncPitches, setSyncPitches] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    document.title = "Executive Command Center | ItzGreen Music";
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch('/api/admin/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIsAuthenticated(true);
          // Load initial data
          await loadSubmissions();
          await loadSyncPitches();
        }
      } else {
        const errorData = await response.json();
        alert(errorData.detail || "Invalid password");
      }
    } catch (error) {
      alert("Authentication failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      const response = await fetch('/api/intake/submissions');
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  };

  const loadSyncPitches = async () => {
    try {
      const response = await fetch('/api/admin/sync-pitches');
      if (response.ok) {
        const data = await response.json();
        setSyncPitches(data.pitches);
      }
    } catch (error) {
      console.error('Error loading sync pitches:', error);
    }
  };

  const toggleReconciliation = (id, field) => {
    setSubmissions(prev => prev.map(sub => 
      sub.id === id ? { ...sub, [field]: !sub[field] } : sub
    ));
  };

  const addNewPitch = async () => {
    const title = prompt("Enter project title:");
    const supervisor = prompt("Enter music supervisor name:");
    const network = prompt("Enter network/client:");
    
    if (title && supervisor && network) {
      try {
        const response = await fetch('/api/admin/sync-pitches', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ title, supervisor, network })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setSyncPitches(prev => [...prev, data.pitch]);
          }
        }
      } catch (error) {
        console.error('Error creating pitch:', error);
        alert('Failed to create pitch. Please try again.');
      }
    }
  };

  const movePitch = async (id, newStatus) => {
    try {
      const response = await fetch(`/api/admin/sync-pitches/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setSyncPitches(prev => prev.map(pitch => 
          pitch.id === id ? { ...pitch, status: newStatus } : pitch
        ));
      }
    } catch (error) {
      console.error('Error updating pitch:', error);
    }
  };

  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-[rgb(17,17,19)] flex items-center justify-center relative overflow-hidden">
        {/* Background Grid */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-8 w-full max-w-md relative z-10"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[rgba(218,255,1,0.1)] border border-[rgba(218,255,1,0.3)] rounded-lg px-4 py-2 mb-6">
              <Shield size={16} className="text-[rgb(218,255,1)]" />
              <span className="text-[rgb(218,255,1)] text-xs tracking-[0.2em] font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                EXECUTIVE ACCESS
              </span>
            </div>
            
            <h1
              className="text-white text-2xl font-bold mb-2"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              Command Center
            </h1>
            <p
              className="text-[rgb(161,161,170)] text-sm"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Managing Director Authentication Required
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[rgb(161,161,170)] text-xs tracking-[0.1em] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                EXECUTIVE PASSWORD
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 pr-12 text-white text-sm focus:border-[rgb(218,255,1)] focus:outline-none transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[rgb(161,161,170)] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-sm font-bold tracking-wider py-3 rounded-xl hover:bg-[rgb(166,190,21)] disabled:opacity-50 transition-all duration-300"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {loading ? 'AUTHENTICATING...' : 'ACCESS DASHBOARD'}
            </button>
          </form>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-20 pb-20 relative overflow-hidden">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1
                className="text-white text-3xl sm:text-4xl font-bold mb-2"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                Executive Command Center
              </h1>
              <p
                className="text-[rgb(161,161,170)] text-sm"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Managing Director Dashboard - Real-time sync operations & catalog oversight
              </p>
            </div>
            
            <button
              onClick={() => setIsAuthenticated(false)}
              className="bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] text-red-400 px-4 py-2 rounded-lg text-sm hover:bg-[rgba(239,68,68,0.2)] transition-colors"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              LOGOUT
            </button>
          </div>

          {/* View Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveView("clearance")}
              className={`px-6 py-3 rounded-xl text-sm font-medium tracking-wider transition-all ${
                activeView === "clearance"
                  ? "bg-[rgb(218,255,1)] text-[rgb(17,17,19)]"
                  : "bg-[rgb(26,28,30)] text-[rgb(161,161,170)] border border-[rgba(255,255,255,0.08)] hover:text-white"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <Database size={16} className="inline mr-2" />
              CLEARANCE LEDGER
            </button>
            <button
              onClick={() => setActiveView("sync")}
              className={`px-6 py-3 rounded-xl text-sm font-medium tracking-wider transition-all ${
                activeView === "sync"
                  ? "bg-[rgb(218,255,1)] text-[rgb(17,17,19)]"
                  : "bg-[rgb(26,28,30)] text-[rgb(161,161,170)] border border-[rgba(255,255,255,0.08)] hover:text-white"
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <Users size={16} className="inline mr-2" />
              SYNC CRM
            </button>
          </div>
        </motion.div>

        {/* Clearance Ledger View */}
        {activeView === "clearance" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-[rgba(255,255,255,0.08)]">
              <h2 className="text-white text-xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                CATALOG CLEARANCE STATUS
              </h2>
              <p className="text-[rgb(161,161,170)] text-sm mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                Track liability shield verification & PRO reconciliation
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[rgb(17,17,19)]">
                  <tr>
                    <th className="text-left text-[rgb(161,161,170)] text-xs tracking-[0.1em] p-4 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                      TRACK TITLE
                    </th>
                    <th className="text-left text-[rgb(161,161,170)] text-xs tracking-[0.1em] p-4 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                      ARTIST
                    </th>
                    <th className="text-left text-[rgb(161,161,170)] text-xs tracking-[0.1em] p-4 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                      IPI NUMBER
                    </th>
                    <th className="text-center text-[rgb(161,161,170)] text-xs tracking-[0.1em] p-4 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                      BMI RECONCILED
                    </th>
                    <th className="text-center text-[rgb(161,161,170)] text-xs tracking-[0.1em] p-4 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                      MLC RECONCILED
                    </th>
                    <th className="text-center text-[rgb(161,161,170)] text-xs tracking-[0.1em] p-4 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                      VAULT ACTIVE
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <tr key={submission.id} className="border-t border-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.02)]">
                      <td className="p-4">
                        <span className="text-white text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {submission.track_title}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-[rgb(200,200,210)] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {submission.artist_name}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-[rgb(161,161,170)] text-sm font-mono">
                          {submission.pro_ipi_number}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => toggleReconciliation(submission.id, 'bmi_reconciled')}
                          className={`w-12 h-6 rounded-full transition-all ${
                            submission.bmi_reconciled
                              ? 'bg-[rgb(218,255,1)]'
                              : 'bg-[rgb(63,63,63)]'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            submission.bmi_reconciled ? 'translate-x-7' : 'translate-x-1'
                          }`} />
                        </button>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => toggleReconciliation(submission.id, 'mlc_reconciled')}
                          className={`w-12 h-6 rounded-full transition-all ${
                            submission.mlc_reconciled
                              ? 'bg-[rgb(218,255,1)]'
                              : 'bg-[rgb(63,63,63)]'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            submission.mlc_reconciled ? 'translate-x-7' : 'translate-x-1'
                          }`} />
                        </button>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => toggleReconciliation(submission.id, 'vault_active')}
                          className={`w-12 h-6 rounded-full transition-all ${
                            submission.vault_active
                              ? 'bg-[rgb(218,255,1)]'
                              : 'bg-[rgb(63,63,63)]'
                          }`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            submission.vault_active ? 'translate-x-7' : 'translate-x-1'
                          }`} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Sync CRM View */}
        {activeView === "sync" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white text-xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  B2B SYNC PIPELINE
                </h2>
                <p className="text-[rgb(161,161,170)] text-sm mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Active pitch tracking & music supervisor relationships
                </p>
              </div>
              <button
                onClick={addNewPitch}
                className="bg-[rgb(218,255,1)] text-[rgb(17,17,19)] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[rgb(166,190,21)] transition-colors flex items-center gap-2"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <Plus size={16} />
                ADD PITCH
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {["Target Identified", "Pitch Deployed", "Vault Accessed", "Active Brief"].map((status) => (
                <div key={status} className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl">
                  <div className="p-4 border-b border-[rgba(255,255,255,0.08)]">
                    <h3 className="text-white text-sm font-bold tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {status.toUpperCase()}
                    </h3>
                    <div className="text-[rgb(161,161,170)] text-xs mt-1">
                      {syncPitches.filter(p => p.status === status).length} projects
                    </div>
                  </div>
                  
                  <div className="p-4 space-y-3">
                    {syncPitches
                      .filter(pitch => pitch.status === status)
                      .map((pitch) => (
                        <div
                          key={pitch.id}
                          className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.05)] rounded-lg p-3 hover:border-[rgba(218,255,1,0.2)] transition-colors cursor-pointer group"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-white text-sm font-medium leading-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
                              {pitch.title}
                            </h4>
                            <button className="opacity-0 group-hover:opacity-100 text-[rgb(161,161,170)] hover:text-white transition-all">
                              <MoreVertical size={14} />
                            </button>
                          </div>
                          
                          <div className="space-y-1">
                            <div className="text-[rgb(161,161,170)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                              <strong>Supervisor:</strong> {pitch.supervisor}
                            </div>
                            <div className="text-[rgb(161,161,170)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                              <strong>Network:</strong> {pitch.network}
                            </div>
                          </div>

                          <div className="flex gap-1 mt-3">
                            {["Target Identified", "Pitch Deployed", "Vault Accessed", "Active Brief"].map((newStatus) => (
                              <button
                                key={newStatus}
                                onClick={() => movePitch(pitch.id, newStatus)}
                                className={`flex-1 text-xs py-1 px-2 rounded transition-colors ${
                                  pitch.status === newStatus
                                    ? 'bg-[rgb(218,255,1)] text-[rgb(17,17,19)]'
                                    : 'bg-[rgba(255,255,255,0.05)] text-[rgb(161,161,170)] hover:bg-[rgba(255,255,255,0.1)]'
                                }`}
                                style={{ fontFamily: "'Inter', sans-serif" }}
                              >
                                {newStatus.split(' ')[0]}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default AdminPage;